import apiClient from './apiClient';

class AuthService {
  // Login user
  async login(email, password) {
    try {
      const response = await apiClient.post('/auth/login', {
        email,
        password,
      });

      // Check if 2FA is required
      if (response.data.requiresTwoFactor) {
        return {
          success: true,
          requiresTwoFactor: true,
          email: response.data.data.email,
          message: response.data.message || 'Please enter your 2FA code'
        };
      }

      const { token, user } = response?.data?.data;

      if (token) {
        localStorage.setItem('authToken', token);
        localStorage.setItem('userInfo', JSON.stringify(user));
      }

      return {
        success: true,
        data: { token, user },
        message: 'Login successful'
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed. Please check your credentials.',
        error: error.response?.data
      };
    }
  }

  // Logout user
  logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    // Optionally call logout endpoint
    // await apiClient.post('/auth/logout');
  }

  // Get current user info
  getCurrentUser() {
    const userInfo = localStorage.getItem('userInfo');
    return userInfo ? JSON.parse(userInfo) : null;
  }

  // Get auth token
  getToken() {
    return localStorage.getItem('authToken');
  }

  // Get user profile from server
  async getProfile() {
    try {
      const response = await apiClient.get('/auth/profile');
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to load profile'
      };
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getToken();
    const userInfo = this.getCurrentUser();
    
    if (!token || !userInfo) {
      return false;
    }
    
    // Optional: Add token expiration check here
    // You could decode JWT token and check exp claim
    
    return true;
  }

  // Check if token is expired (if using JWT)
  isTokenExpired() {
    const token = this.getToken();
    if (!token) return true;
    
    try {
      // If using JWT, you can decode and check expiration
      // const payload = JSON.parse(atob(token.split('.')[1]));
      // return payload.exp * 1000 < Date.now();
      
      // For now, just return false (not expired)
      return false;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }

  // Refresh token (if needed)
  async refreshToken() {
    try {
      const response = await apiClient.post('/auth/refresh');
      const { token } = response.data;
      
      if (token) {
        localStorage.setItem('authToken', token);
        return token;
      }
    } catch (error) {
      this.logout();
      throw error;
    }
  }

  // Register user (optional)
  async register(userData) {
    try {
      const response = await apiClient.post('/auth/register', userData);
      return {
        success: true,
        data: response.data,
        message: 'Registration successful'
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed',
        error: error.response?.data
      };
    }
  }

  // Forgot password
  async forgotPassword(email) {
    try {
      await apiClient.post('/auth/forgot-password', { email });
      return {
        success: true,
        message: 'Password reset email sent'
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send reset email'
      };
    }
  }

  // Two-Factor Authentication Methods
  
  // Verify 2FA code during login
  async verify2FALogin(email, token, isBackupCode = false) {
    try {
      const response = await apiClient.post('/auth/2fa/verify-login', {
        email,
        token,
        isBackupCode
      });

      const { token: authToken, user } = response?.data?.data;

      if (authToken) {
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('userInfo', JSON.stringify(user));
      }

      return {
        success: true,
        data: { token: authToken, user },
        message: 'Login successful'
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || '2FA verification failed'
      };
    }
  }

  // Setup 2FA - get QR code
  async setup2FA() {
    try {
      const response = await apiClient.post('/auth/2fa/setup');
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to setup 2FA'
      };
    }
  }

  // Verify 2FA setup
  async verify2FASetup(token) {
    try {
      const response = await apiClient.post('/auth/2fa/verify-setup', { token });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to verify 2FA'
      };
    }
  }

  // Disable 2FA
  async disable2FA(password) {
    try {
      const response = await apiClient.post('/auth/2fa/disable', { password });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to disable 2FA'
      };
    }
  }

  // Regenerate backup codes
  async regenerateBackupCodes(password) {
    try {
      const response = await apiClient.post('/auth/2fa/regenerate-backup-codes', { password });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to regenerate backup codes'
      };
    }
  }

  // Change password
  async changePassword(currentPassword, newPassword) {
    try {
      const response = await apiClient.put('/auth/change-password', {
        currentPassword,
        newPassword
      });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to change password'
      };
    }
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;